/**
 * Botschaften an die Regie: kurze Video- oder Sprachnachrichten, die
 * Athlet:innen und Zuschauer:innen aufnehmen und hochladen, wenn sie gerade
 * nicht live geschaltet werden koennen -- die Freigabe dauert, oder es
 * passiert etwas, das sonst niemand sieht.
 *
 * Der Weg einer Botschaft:
 *
 *   aufgenommen -> hochgeladen -> aufbereitet (`processing`) -> neu (`pending`)
 *     -> freigegeben (`approved`, wahlweise Favorit) -> im Stream gespielt
 *     -> oder geloescht: eine Stunde im Papierkorb (`trash`), dann weg
 *
 * Gespielt wird eine Botschaft wie ein Werbespot: gross im Bild, das Live-Bild
 * klein in der Ecke, dazu ein Namensschild statt „Werbung" -- siehe
 * `AdBreak.clip`. Ueber die Botschaft entscheidet allein die Regie; was neu
 * ist, sieht niemand sonst.
 */
import type { Role } from './index.ts';

/** Laenger darf eine Botschaft nicht sein -- die Aufnahme stoppt hier von selbst. */
export const MAX_CLIP_SECONDS = 90;
/**
 * Groesste Datei. Eine Aufnahme in der App hat gut 55 MB; ein 4K-Video aus der
 * Galerie des iPhones ueber 90 s eher 250-400 MB -- auch das soll gehen, es
 * wird ohnehin auf 1080p verkleinert. Der Upload geht als Strom auf die
 * Platte, die Groesse kostet also Zeit, keinen Speicher.
 */
export const MAX_CLIP_UPLOAD_BYTES = 500 * 1024 * 1024;
/** So lange liegt eine geloeschte Botschaft im Papierkorb. */
export const CLIP_TRASH_MS = 60 * 60_000;
/** So lange bleiben Botschaften nach dem Ende des Events. */
export const CLIP_KEEP_AFTER_EVENT_MS = 7 * 24 * 3600_000;
/** So viele Botschaften darf eine Zuschauerin schicken, bevor die Regie mehr erlaubt. */
export const SPECTATOR_CLIP_LIMIT = 3;
/** Um so viele erhoeht „weitere erlauben" die Grenze. */
export const CLIP_LIMIT_STEP = 3;

export type ClipKind = 'video' | 'audio';

/**
 * Von wem die Regie Botschaften annimmt. „Nur vor Ort" gilt fuer
 * Zuschauer:innen: wer weit weg vor dem Bildschirm sitzt, schickt sonst
 * genauso -- und bei einem grossen Event ueberflutet das die Regie.
 * Athlet:innen sind ohnehin an der Strecke und immer ausgenommen. Ohne
 * Event-Bereich laesst sich „vor Ort" nicht pruefen; dann gilt „Alle".
 */
export type ClipPolicy = 'all' | 'local' | 'off';

export const CLIP_POLICIES: { id: ClipPolicy; label: string; hint: string }[] = [
  { id: 'all', label: 'Alle', hint: 'Jede:r, der das Event verfolgt, kann schicken.' },
  {
    id: 'local',
    label: 'Nur vor Ort',
    hint: 'Zuschauer:innen nur aus dem Event-Bereich (der Browser fragt nach dem Standort). Athlet:innen immer.',
  },
  { id: 'off', label: 'Aus', hint: 'Gerade nimmt die Regie keine Botschaften an.' },
];

export const clipKindLabel: Record<ClipKind, string> = {
  video: 'Videobotschaft',
  audio: 'Sprachnachricht',
};

/** Wer die Botschaft geschickt hat -- so, wie es im Namensschild steht. */
export interface ClipFrom {
  name: string;
  role: Role;
  bib?: string;
}

/** „Anna Muster · Startnr. 12" bzw. „Max · Zuschauer:in". */
export function clipFromLabel(from: ClipFrom): string {
  if (from.bib) return `${from.name} · Startnr. ${from.bib}`;
  return from.role === 'spectator' ? `${from.name} · Zuschauer:in` : from.name;
}

/* ------------------------------------------------------------- Regie */

export type ClipStatus = 'processing' | 'pending' | 'approved' | 'trash';

/** Eine Botschaft, wie die Regie sie sieht. */
export interface ClipView {
  id: string;
  kind: ClipKind;
  status: ClipStatus;
  /** Zusaetzlich markiert -- steht oben in „Freigegeben". */
  favorite: boolean;
  from: ClipFrom;
  /** Absender, ueber den „blockieren" und „weitere erlauben" laufen. */
  senderKey: string;
  createdAt: number;
  durationMs: number;
  /** Zum Ansehen in der Regie -- null, solange sie noch aufbereitet wird. */
  url: string | null;
  /** Im Papierkorb seit -- endgueltig weg ist sie `CLIP_TRASH_MS` danach. */
  trashedAt: number | null;
  /** Zuletzt im Stream gespielt. */
  airedAt: number | null;
}

/** Ein Absender. Grenzen gibt es nur fuer Zuschauer:innen. */
export interface ClipSenderView {
  key: string;
  name: string;
  role: Role;
  /** Geschickte Botschaften -- geloeschte zaehlen mit, sonst waere die Grenze leicht zu umgehen. */
  sent: number;
  /** Erlaubte Botschaften; null heisst ohne Grenze. */
  allowed: number | null;
  blocked: boolean;
  /** Hat die Grenze erreicht und wollte noch eine schicken. */
  wantsMore: boolean;
}

export interface ClipsState {
  clips: ClipView[];
  senders: ClipSenderView[];
  policy: ClipPolicy;
  /** Hat das Event einen Bereich? Ohne ihn gibt es kein „Nur vor Ort". */
  zone: boolean;
}

export const emptyClips = (): ClipsState => ({ clips: [], senders: [], policy: 'all', zone: false });

/** Reihenfolge in „Freigegeben": Favoriten oben, sonst die neueste zuerst. */
export const sortApproved = (a: ClipView, b: ClipView): number =>
  Number(b.favorite) - Number(a.favorite) || b.createdAt - a.createdAt;

/* ---------------------------------------------------------- Absender */

/**
 * Der Stand einer eigenen Botschaft. Dass die Regie eine geloescht hat, sieht
 * der Absender nicht -- fuer ihn bleibt sie „eingegangen".
 */
export type MyClipState = 'processing' | 'received' | 'approved' | 'onAir' | 'aired' | 'failed';

export const myClipStateLabel: Record<MyClipState, string> = {
  processing: 'wird aufbereitet',
  received: 'eingegangen',
  approved: 'freigegeben',
  onAir: 'läuft gerade im Stream',
  aired: 'im Stream gezeigt',
  failed: 'ließ sich nicht verarbeiten',
};

export interface MyClip {
  id: string;
  kind: ClipKind;
  createdAt: number;
  durationMs: number;
  state: MyClipState;
  /** Nur bei `failed`: warum. */
  error?: string;
}

export interface MyClipsState {
  clips: MyClip[];
  sent: number;
  /** null heisst ohne Grenze. */
  allowed: number | null;
  blocked: boolean;
  /** Schon bei der Regie um weitere gebeten (`clip:ask`). */
  wantsMore: boolean;
  policy: ClipPolicy;
  /** Von „Nur vor Ort" ausgenommen -- Athlet:innen. */
  exempt: boolean;
}

export const emptyMyClips = (): MyClipsState => ({
  clips: [],
  sent: 0,
  allowed: null,
  blocked: false,
  wantsMore: false,
  policy: 'all',
  exempt: false,
});

/** Muss dieser Absender erst zeigen, dass er vor Ort ist? */
export const clipNeedsLocation = (mine: MyClipsState): boolean => mine.policy === 'local' && !mine.exempt;

/** Grenze erreicht -- dann kann der Absender bei der Regie um weitere bitten. */
export const clipLimitReached = (mine: MyClipsState): boolean => mine.allowed !== null && mine.sent >= mine.allowed;

/** Darf dieser Absender gerade eine Botschaft schicken? Null heisst ja, sonst der Grund. */
export function clipRefusal(mine: MyClipsState): string | null {
  if (mine.policy === 'off') return 'Die Regie nimmt gerade keine Botschaften an.';
  if (mine.blocked) return 'Die Regie nimmt von dir gerade keine Botschaften an.';
  if (clipLimitReached(mine)) {
    return mine.wantsMore
      ? `Du hast ${mine.sent} Botschaften geschickt. Die Regie weiß, dass du gern noch eine schicken möchtest.`
      : `Du hast ${mine.sent} Botschaften geschickt. Weitere kann die Regie dir erlauben.`;
  }
  return null;
}

/** Laufzeit als `1:05`. */
export function formatClipTime(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
